import { Button, Intent } from "@blueprintjs/core";
import {
  FGCustomInput,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import * as Yup from "yup";

import { KennelApi, KennelDto } from "../../../../api";
import {
  FGCountrySelect,
  FGSRSHCheckboxInput,
  SharepointButton,
  SmallFormGenerator,
  FGSRSHDateMaskInput,
} from "../../../../components";
import { useDialog } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";

export interface IKennelDetailPageProps {
  kennelId: number;
  editMode: boolean;
}

export const KennelDetailPage: React.FunctionComponent<IKennelDetailPageProps> = ({ kennelId, editMode }) => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(KennelApi);
  const { openNewTab } = useWindow();
  const history = useHistory();
  const { showDialogPromise } = useDialog();
  const [closeKennelLoading, setcloseKennelLoading] = React.useState(false);
  const [kennelTypes, tLoading] = useReferential((a) => a.getKennelTypes(), false);
  const [translatedKennelTypes, setTranslatedKennelTypes] = React.useState([]);
  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi<KennelDto>(
    {
      getApiFn: (id = kennelId) => (id > 0 ? api.get(id) : new KennelDto().toJSON()),
      saveApiFn: (d) => api.save(d),
      onSavedRoute: (d) => `${ERoutes.kennel}/${d.kennelId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.kennelId),
      onDeletedRoute: () => ERoutes.kennel,
    },
    [kennelId]
  );

  const [previousKennelName, setPreviousKennelName] = React.useState<string>();

  React.useEffect(() => {
    data?.previousKennelId && api.getDisplayName(data?.previousKennelId).then((c) => setPreviousKennelName(c));
  }, [api, data]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string().nullable().required(t(ETLCodes.Required)),
      countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      type: Yup.string().nullable().required(t(ETLCodes.Required)),
      originCountryId: Yup.number().nullable(),
    });
  }, [t]);

  React.useEffect(() => {
    if (!tLoading && kennelTypes) {
      const newKennelType = kennelTypes.map((c) => {
        return { label: tUnsafe(c.label), value: c.value };
      });
      setTranslatedKennelTypes(newKennelType);
    }
  }, [kennelTypes, tLoading, tUnsafe]);
  const closeKennel = React.useCallback(async () => {
    const dialogResult = await showDialogPromise({
      title: t(ETLCodes.CloseKennelConfirmationTitle),
      message: t(ETLCodes.CloseKennelConfirmationMessage),
    });
    if (dialogResult !== "yes") return;
    setcloseKennelLoading(true);
    try {
      await api.closeKennel(kennelId);
      refresh();
      showSuccess(t(ETLCodes.KennelClosed));
      setcloseKennelLoading(false);
    } catch {
      showError(t(ETLCodes.Error));
      setcloseKennelLoading(false);
    }
  }, [api, kennelId, refresh, showDialogPromise, t]);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.sharepointUri) {
      window.open(data?.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(kennelId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.sharepointUri, api, kennelId, refresh]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.kennel)}
        onDelete={deleteItem}
        showDeleteButton={kennelId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup
          fieldsetProps={{
            rightElement: kennelId > 0 && (
              <>
                <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />{" "}
                {data?.isClosed === false && (
                  <Button
                    onClick={closeKennel}
                    text={t(ETLCodes.CloseKennel)}
                    intent={Intent.WARNING}
                    loading={closeKennelLoading}
                  />
                )}
              </>
            ),
          }}
          title={t(ETLCodes.Kennel)}
        >
          {data?.previousKennelId && (
            <FGCustomInput name="previousKennelId" label={t(ETLCodes.PreviousKennel)}>
              {(ctx) => (
                <Link target="_blank" to={`${ERoutes.kennel}/${data?.previousKennelId}/detail/view`}>
                  {previousKennelName}
                </Link>
              )}
            </FGCustomInput>
          )}
          <FGTextInput name="name" label={t(ETLCodes.OfficialName)} maxLength={25} />
          <FGSelectInput name="type" label={t(ETLCodes.Type)} items={translatedKennelTypes} loading={tLoading} />
          <FGCountrySelect
            name="countryId"
            label={t(ETLCodes.Country)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
          />
          <FGTextInput name="fciNumber" label={t(ETLCodes.FciNumber)} />
          <FGTextInput name="dossierId" label={t(ETLCodes.RequestAffix)} />
          <FGSRSHDateMaskInput name="attributionDate" label={t(ETLCodes.AttributionDate)} />
          <FGSRSHDateMaskInput name="publishingDate" label={t(ETLCodes.PublishingDate)} />
          <FGCountrySelect
            name="originCountryId"
            label={t(ETLCodes.CountryOfOrigin)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
          />
          <FieldGroup columns={2}>
            <FGSRSHDateMaskInput name="exclusionStartDate" label={t(ETLCodes.ExclusionStartDate)} />
            <FGSRSHDateMaskInput name="exclusionEndDate" label={t(ETLCodes.ExclusionEndDate)} />
          </FieldGroup>
          <FGTextAreaInput name="exclusionReason" label={t(ETLCodes.ExclusionReason)} />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
          <FGSRSHCheckboxInput name="isClosed" label={t(ETLCodes.Closed)} readonly />
          <FGSRSHDateMaskInput name="closingDate" label={t(ETLCodes.ClosingDate)} readonly />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
